import { add, flush, init } from '@amplitude/analytics-browser';
import { Event } from '@amplitude/analytics-types';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { getStaticPathAndSearch } from 'navigation/location/location';
import { extractQueryParams } from 'navigation/utils';
import { initGTM, sendGoogleTagManagerEvent } from 'utils/google-tag-manager';

export {
  getSessionId,
  getDeviceId,
  getUserId,
  identify,
  Identify,
  revenue,
  Revenue,
  setDeviceId,
  setOptOut,
  setSessionId,
  setUserId,
  track,
  Types,
} from '@amplitude/analytics-browser';

export const initAmplitudeWithPluginsForPlatform = async (
  amplitudeKey: string,
  cognitoId: string | undefined,
  options: any
): Promise<void> => {
  await add(
    sessionReplayPlugin({
      // remote configuration seems to be implicitly enabled by default unlike react native's config
      sampleRate: 0.2,
    })
  ).promise;

  // init() emits the initial session event, but GTM must register after init so the Identify result
  // reflects Amplitude (the Analytics SDK resolves an event with the first destination result), and
  // a destination never receives events processed before it registered. Capture the session event
  // with an enrichment plugin, which does not compete for the destination result, so it can be
  // replayed to GTM.
  let capturedSessionStartEvent: Event | undefined;
  await add({
    name: 'session-start-capture',
    type: 'enrichment',
    execute: async (event: Event): Promise<Event> => {
      if (event.event_type === 'session_start') {
        capturedSessionStartEvent = event;
      }
      return event;
    },
  }).promise;

  await init(amplitudeKey, cognitoId, options).promise;

  // Drain the timeline so the session event tracked during init passes through the capture plugin
  // and reaches Amplitude before GTM registers, so GTM cannot then receive it a second time.
  await flush().promise;

  // Register Amplitude's own destination before GTM so Identify success reflects Amplitude delivery.
  await add({
    name: 'google-tag-manager',
    type: 'destination',
    setup: initGTM,
    execute: sendGoogleTagManagerEvent,
  }).promise;

  // GTM registered after init emitted the session event, so replay the captured event to deliver it
  // to GTM exactly once without moving GTM ahead of Amplitude in the destination result order.
  if (capturedSessionStartEvent) {
    await sendGoogleTagManagerEvent(capturedSessionStartEvent);
    capturedSessionStartEvent = undefined;
  }

  await add(engagementPlugin()).promise;

  // Set UTM parameters for web
  // The UTM parameters in mobile are handled by
  // the native intent + branch deep linking
  // Web does not have a native intent + branch deep linking,
  // so we need to set the UTM parameters here
  const uri = getStaticPathAndSearch();
  const utmParams = extractQueryParams(uri);
  const { setUTMParams } = await import('./index');
  setUTMParams(utmParams);
};
